import axios from "axios";
import { encryptData, decryptData } from "./dataEncoder";
import {
  isRequestEncodingEnabled,
  isResponseDecodingEnabled,
  shouldSkipRoute,
  shouldSkipMethod,
  shouldSkipContentType,
} from "../Config/encoding";
import { isTokenExpired, clearTokenData } from "./tokenValidator";

const api = (token, cancelToken, isHandlerDisabled, isBlob) => {
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URI,
    headers:
      typeof token === "string"
        ? { Authorization: `Bearer ${token}` }
        : { Authorization: `Bearer ${process.env.REACT_APP_BACKEND_URI}` },
    withCredentials: true,
    cancelToken,
    responseType: isBlob ? "blob" : "json",
  });

  if (!isHandlerDisabled) {
    axiosInstance.interceptors.request.use(
      (config) => {
        if (token && isTokenExpired()) {
          console.warn("Token expired, clearing localStorage");
          clearTokenData();

          if (typeof window !== "undefined") {
            window.location.href = "/";
          }

          return Promise.reject(new Error("Token expired"));
        }

        const encodingOn =
          isRequestEncodingEnabled() || isResponseDecodingEnabled();

        if (!encodingOn) {
          return config;
        }

        if (shouldSkipRoute(config.url)) {
          return config;
        }

        const isFormData = config.data instanceof FormData;
        const isFileBody =
          config.data instanceof Blob || config.data instanceof File;
        const contentType = config.headers?.["Content-Type"] || "";
        const skipContentType = shouldSkipContentType(contentType);
        const skipBodyEncrypt =
          isBlob ||
          !config.data ||
          isFormData ||
          isFileBody ||
          skipContentType ||
          !isRequestEncodingEnabled() ||
          shouldSkipMethod(config.method);

        if (!skipBodyEncrypt) {
          try {
            const encryptedData = encryptData(config.data);
            config.data = {
              encrypted: true,
              data: encryptedData,
            };
          } catch (error) {
            console.error("Failed to encrypt request data:", error);
          }
        }

        if (!isBlob && isResponseDecodingEnabled()) {
          config.headers = config.headers || {};
          config.headers["X-Data-Encrypted"] = "true";
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        if (!isResponseDecodingEnabled()) {
          return response.data;
        }

        if (!isBlob && response.data) {
          try {
            if (
              response.data.encrypted === true &&
              response.data.data &&
              typeof response.data.data === "string"
            ) {
              try {
                response.data = decryptData(response.data.data);
              } catch (decryptError) {
                console.error("Decryption failed:", decryptError.message);
                throw new Error("Decryption failed - check encryption keys");
              }
            }
          } catch (error) {
            console.error("Response processing error:", error);
            throw error;
          }
        }

        const newToken = response.headers?.["x-new-token"];
        const tokenRefreshed = response.headers?.["x-token-refreshed"];
        const newExpiresAt = response.headers?.["x-token-expires-at"];

        if (tokenRefreshed === "true" && newToken) {
          localStorage.setItem("token", newToken);
          if (newExpiresAt) {
            localStorage.setItem("token_expires_at", newExpiresAt);
          }
        }

        if (
          response.data.status === "error" ||
          response.data.status === "failed"
        ) {
          let error;
          if (
            typeof response.data.message !== "string" &&
            Object.values(response.data.message).length
          )
            error = Object.values(response.data.message).join(", ");
          else error = response.data.message;
          if (
            typeof response.data.error !== "string" &&
            Object.values(response.data.error).length
          )
            error = Object.values(response.data.error).join(", ");
          /* eslint-disable-next-line prefer-destructuring */ else
            error = response.data.error;
          return Promise.reject(error);
        }
        return response.data;
      },
      (_error) => {
        const { response } = _error;
        if (response) {
          if (response.status === 401) {
            localStorage.clear();
            // eslint-disable-next-line no-restricted-globals
            if (location) location.replace("/");
          }

          let errorData = response.data;
          if (
            errorData &&
            errorData.encrypted === true &&
            errorData.data &&
            typeof errorData.data === "string" &&
            isResponseDecodingEnabled()
          ) {
            try {
              errorData = decryptData(errorData.data);
              response.data = errorData;
            } catch (decryptError) {
              console.error("Failed to decrypt error response:", decryptError);
            }
          }

          let error;
          if (
            typeof errorData?.message === "object" &&
            Object.values(errorData?.message || {}).length
          )
            error = Object.values(errorData?.message).join(", ");
          else error = errorData?.message;
          if (
            !error &&
            typeof errorData?.error === "object" &&
            Object.values(errorData?.error || {}).length
          )
            error = Object.values(errorData?.error).join(", ");
          else if (!error) error = errorData?.error;
          if (Array.isArray(error)) error = error.filter(Boolean).join(", ");
          if (!error) {
            error =
              response.status === 404
                ? "Record not found."
                : "Something went wrong. Please try again.";
          }
          return Promise.reject({ ..._error, error });
        }
        return Promise.reject(_error);
      }
    );
  }
  return axiosInstance;
};

export default api;
